import { useEffect, useState, useCallback } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  type User,
} from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getAuthClient, getDb, googleProvider } from '../lib/firebase';

export interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

export function useAuthState(): AuthState {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    const auth = getAuthClient();
    const unsub = onAuthStateChanged(
      auth,
      async (user) => {
        if (user) {
          await upsertUserDoc(user);
        }
        setState({ user, loading: false, error: null });
      },
      (err) => setState({ user: null, loading: false, error: err.message }),
    );
    return () => unsub();
  }, []);

  return state;
}

async function upsertUserDoc(user: User): Promise<void> {
  const db = getDb();
  try {
    await setDoc(
      doc(db, 'users', user.uid),
      {
        userId: user.uid,
        email: user.email ?? '',
        displayName: user.displayName ?? user.email ?? user.uid,
        photoURL: user.photoURL ?? null,
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      },
      { merge: true },
    );
  } catch {
    // Rules may deny if not signed-in yet; harmless in that case.
  }
}

export function useAuthActions() {
  const google = useCallback(async () => {
    const auth = getAuthClient();
    await signInWithPopup(auth, googleProvider);
  }, []);
  const email = useCallback(async (mail: string, pw: string) => {
    const auth = getAuthClient();
    await signInWithEmailAndPassword(auth, mail, pw);
  }, []);
  const register = useCallback(async (mail: string, pw: string) => {
    const auth = getAuthClient();
    await createUserWithEmailAndPassword(auth, mail, pw);
  }, []);
  const logout = useCallback(async () => {
    await signOut(getAuthClient());
  }, []);
  return { google, email, register, logout };
}
